import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Heart, ShoppingCart, Truck } from "lucide-react";

const ProductCard = ({
  product,
  isLiked = false,
  isSelected = false,
  onProductClick,
  onLikeClick,
  onSelectedClick,
  onQuickView,
  showQuickView = true,
  className = "",
  showPrice = true,
  showStock = true,
}) => {
  console.log("ProductCard - product data:", product);
  console.log("ProductCard - segment field:", product?.segment);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const cardRef = useRef(null);

  // Check if product has lot details (regardless of Lot flag)
  const hasLotDetails = (product?.lotDetails?.length || 0) > 0;
  const isLotProduct = product?.Lot === true || hasLotDetails;
  const lotDetails = useMemo(
    () => product?.lotDetails || [],
    [product?.lotDetails],
  );
  const totalImages =
    isLotProduct && lotDetails.length > 0
      ? Math.max(product.images?.length || 0, lotDetails.length)
      : product.images?.length || 0;

  const imgTouchStartX = useRef(null);

  const handleImageTouchStart = (e) => {
    if (totalImages <= 1) return;
    imgTouchStartX.current = e.touches[0].clientX;
  };

  const handleImageTouchEnd = (e) => {
    if (imgTouchStartX.current === null || totalImages <= 1) return;
    const diff = imgTouchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 25) {
      e.stopPropagation();
      if (diff > 0) {
        // Swiped left -> next photo
        setCurrentIndex((prev) => (prev + 1) % totalImages);
      } else {
        // Swiped right -> prev photo
        setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
      }
    }
    imgTouchStartX.current = null;
  };

  // Get current image URL based on lot / image rotation
  const getCurrentImageUrl = useCallback(() => {
    if (!product?.images || product.images.length === 0) return "";

    if (isLotProduct && lotDetails.length > 0) {
      if (product.images.length >= lotDetails.length) {
        return product.images[currentIndex]?.url?.trim() || "";
      }
      const imageIndex = currentIndex % product.images.length;
      return product.images[imageIndex]?.url?.trim() || "";
    }

    if (product.images.length > 1) {
      const imgIdx = currentIndex % product.images.length;
      return product.images[imgIdx]?.url?.trim() || "";
    }

    return product.images[0]?.url?.trim() || "";
  }, [product, isLotProduct, lotDetails, currentIndex]);

  // Get current weight based on lot rotation
  const getCurrentWeight = useCallback(() => {
    if (isLotProduct && lotDetails.length > 0) {
      const currentLot = lotDetails[currentIndex];
      return currentLot ? `${currentLot.avgNetWt}gm` : `${product.netWt}gm`;
    }
    return `${lotDetails[currentIndex]?.avgNetWt || product?.netWt || ""}gm`;
  }, [isLotProduct, lotDetails, currentIndex, product?.netWt]);

  // Get current size based on lot rotation
  const getCurrentSize = useCallback(() => {
    if (isLotProduct && lotDetails.length > 0) {
      const currentLot = lotDetails[currentIndex];
      return currentLot ? `${currentLot.size}` : "";
    }
    return `${lotDetails[currentIndex]?.size || ""}`;
  }, [isLotProduct, lotDetails, currentIndex]);

  // Get current price from lotDetails
  const getCurrentPrice = useCallback(() => {
    // Always check lotDetails first if they exist
    if (hasLotDetails && lotDetails.length > 0) {
      const currentLot = lotDetails[currentIndex];
      const lotPrice = currentLot?.price;
      if (lotPrice !== undefined && lotPrice !== null && lotPrice !== "") {
        return lotPrice;
      }
    }
    // Fallback to product-level price
    const productPrice = product?.price || product?.sellingPrice;
    return productPrice;
  }, [
    hasLotDetails,
    lotDetails,
    currentIndex,
    product?.price,
    product?.sellingPrice,
  ]);

  const imageUrl = getCurrentImageUrl();
  const displayName = product?.productName || "Product";
  const weightText = `${getCurrentWeight()} ${
    getCurrentSize() ? `(${getCurrentSize()})` : ""
  }`;
  const price = getCurrentPrice();
  const originalPrice = product?.originalPrice || product?.mrp;

  // Segment determination
  const segment = (product?.segment || "").toUpperCase().trim();
  const isNetWtSegment = [
    "GOLD",
    "SILVER",
    "DIAMOND",
    "PLATINUM",
    "BANDHEL",
  ].includes(segment);
  const isCaratSegment =
    segment === "GEMS" ||
    segment === "PEARL" ||
    segment === "PEARLS" ||
    segment === "GEMSTONE" ||
    segment === "GEMSTONES";

  // Get current carat weight based on lot rotation or product fields
  const getCurrentCaratWeight = useCallback(() => {
    const lot =
      isLotProduct && lotDetails.length > 0
        ? lotDetails[currentIndex]
        : lotDetails[currentIndex];
    const val = lot?.avgNetWt;

    if (val === "" || val === null || val === undefined) return "";
    const strVal = String(val).trim();
    if (/ct|carat/i.test(strVal)) {
      return strVal;
    }
    const cleaned = strVal.replace(/gm$/i, "").trim();
    return cleaned ? `${cleaned}ct` : "";
  }, [isLotProduct, lotDetails, currentIndex]);

  const caratWeight = getCurrentCaratWeight();
  const currentSize = getCurrentSize();
  const caratWeightText = caratWeight
    ? `${caratWeight} ${currentSize ? `(${currentSize})` : ""}`.trim()
    : "";

  const formattedMetaPrice = useMemo(() => {
    const currentPrice = getCurrentPrice();
    if (
      currentPrice === undefined ||
      currentPrice === null ||
      currentPrice === ""
    ) {
      return "N/A";
    }
    const nummin = Number(currentPrice) + Number(currentPrice * 0.2);
    const nummax = Number(currentPrice) + Number(currentPrice * 0.3);
    return `~₹${nummin.toLocaleString()} - ₹${nummax.toLocaleString()}`;
  }, [getCurrentPrice]);
  const discount =
    originalPrice && price && originalPrice > price
      ? Math.round(((originalPrice - price) / originalPrice) * 100)
      : 0;
  const stockStatus = product?.stockStatus || product?.availability;
  const isInStock =
    stockStatus?.toLowerCase() !== "out of stock" &&
    stockStatus?.toLowerCase() !== "unavailable";

  // Auto-switch variants for Lot products (if multiple)
  useEffect(() => {
    if (!isLotProduct || lotDetails.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % lotDetails.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isLotProduct, lotDetails.length]);

  const handleCardClick = useCallback(() => {
    if (onProductClick && product) onProductClick(product);
  }, [onProductClick, product]);

  const handleLikeClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (onLikeClick && product?.id) onLikeClick(e, product.id);
    },
    [onLikeClick, product?.id],
  );

  const handleSelectedClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (onSelectedClick && product) onSelectedClick(e, product);
    },
    [onSelectedClick, product],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick],
  );

  const showProgressIndicators = totalImages > 1;

  return (
    <article
      ref={cardRef}
      className={`product-card ${className} ${isSelected ? "selected" : ""} ${
        !isInStock ? "out-of-stock" : ""
      }`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View ${displayName}${
        totalImages > 1 ? `, ${totalImages} photos available` : ""
      }`}
      aria-pressed={isSelected}
    >
      {/* Product Image */}
      <div
        className="product-image"
        onTouchStart={handleImageTouchStart}
        onTouchEnd={handleImageTouchEnd}
      >
        {imageUrl && !imageError ? (
          <>
            <img
              src={imageUrl}
              alt={displayName}
              loading="lazy"
              onError={() => setImageError(true)}
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
              }}
            />
            {/* Progress Dots for multiple photos / variants */}
            {showProgressIndicators && (
              <div
                className="stepper-wrapper"
                aria-label={`Photo ${currentIndex + 1} of ${totalImages}`}
              >
                {Array.from({ length: totalImages }).map((_, index) => (
                  <button
                    key={index}
                    className={`progress-dot ${
                      index === currentIndex ? "active" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    aria-label={`View photo ${index + 1}`}
                    aria-current={index === currentIndex ? "true" : "false"}
                    type="button"
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="image-placeholder" aria-hidden="true">
            <span>No Image Available</span>
          </div>
        )}

        {/* Like Button */}
        <button
          className={`like-btn ${isLiked ? "liked" : ""}`}
          onClick={handleLikeClick}
          aria-label={isLiked ? "Remove from favorites" : "Add to favorites"}
          aria-pressed={isLiked}
          type="button"
        >
          <Heart size={16} fill={isLiked ? "currentColor" : "none"} />
        </button>

        {/* Badges */}
        <div className="badges-wrapper">
          {discount > 0 && (
            <div className="discount-badge" aria-label={`${discount}% off`}>
              -{discount}%
            </div>
          )}
          {!isInStock && showStock && (
            <div className="out-of-stock-badge" aria-label="Out of stock">
              <Truck size={10} />
              <span>Out of Stock</span>
            </div>
          )}
        </div>
      </div>

      {/* Product Details */}
      <div className="product-details">
        <div className="product-header">
          <h3 className="product-name">{displayName}</h3>
        </div>

        <div className="product-meta">
          <span className="product-spec">
            {product.styleType} / {product.specification}
          </span>
          {isNetWtSegment ? (
            <span className="product-weight">Net wt: {weightText}</span>
          ) : isCaratSegment ? (
            <span className="product-weight">
              Carat wt: {caratWeightText || "-"}
            </span>
          ) : (
            <span className="product-weight">Price:{formattedMetaPrice}/-</span>
          )}
        </div>
        {showStock && stockStatus && (
          <div
            className={`stock-status ${
              isInStock ? "in-stock" : "out-of-stock"
            }`}
          >
            <span className="stock-indicator" aria-hidden="true"></span>
            <span>{isInStock ? "In Stock" : "Out of Stock"}</span>
          </div>
        )}

        {/* Select Button */}
        <button
          className={`selectbtn ${isSelected ? "selected" : ""} ${
            !isInStock ? "disabled" : ""
          }`}
          onClick={handleSelectedClick}
          disabled={!isInStock}
          aria-label={isSelected ? "Remove from selection" : "Add to selection"}
          aria-pressed={isSelected}
          type="button"
        >
          {isSelected ? (
            <>
              <ShoppingCart size={16} />
              <span>SELECTED</span>
            </>
          ) : (
            <span>SELECT</span>
          )}
        </button>
      </div>
    </article>
  );
};

ProductCard.defaultProps = {
  isSelected: false,
  showQuickView: true,
  showPrice: true,
  showStock: true,
  onProductClick: () => {},
  onLikeClick: () => {},
  onSelectedClick: () => {},
  onQuickView: () => {},
};

export default ProductCard;
